"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HoverGlow, trackHoverGlow } from "./_lib/HoverGlow";
import { useIsMobile } from "./_lib/useMediaQuery";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";
import { setMotionLayers } from "./_lib/motionLayer";
import OptimizedPicture from "./_lib/OptimizedPicture";

gsap.registerPlugin(ScrollTrigger);

const PRINCIPLES = [
  {
    title: "Built on proven learning science",
    description:
      "Spaced repetition. Active recall. Courseware-aligned content. Five minutes of daily practice outside the classroom that reinforces the syllabus, never pulls students away from it.",
    image: "/assets/imags/1.webp",
  },
  {
    title: "Helps teachers, without extra work",
    description:
      "The app extends lessons into students' pockets. They practise grammar, vocabulary, and pronunciation on their own time. Teacher sees who's engaged, without creating or assigning a single task.",
    image: "/assets/imags/2.webp",
  },
  {
    title: "Grounded in Global Scale of English (GSE)",
    description:
      "Every grammar, vocabulary, and pronunciation activity is mapped to GSE objectives. Progress you can measure, not just minutes on screen.",
    image: "/assets/imags/3.webp",
  },
  {
    title: "Fits students' mobile-native lives",
    description:
      "Practice that lives outside the classroom. Students pick up their phone for 5 minutes at home, on the bus, anywhere they have a spare moment, and put it down. The app is built for exactly that.",
    image: "/assets/imags/4.webp",
  },
];

const SCROLL_VH_PER_CARD_DESKTOP = 100;
const SCROLL_VH_PER_CARD_MOBILE = 65;

const CARD_STAGGER_DESKTOP = 10;
const CARD_STAGGER_MOBILE = 10;
const CARD_SCALE_STEP = 0.04;
const CARD_OPACITY_STEP = 0.2;

function stackedCardOpacity(depth: number) {
  return Math.max(0, 1 - depth * CARD_OPACITY_STEP);
}

export default function CorePrinciples() {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isMobileLayout = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [isMobileLayout]);

  useEffect(() => {
    const section = sectionRef.current;
    const stack = stackRef.current;
    const pin = pinRef.current;
    if (!section || !stack || !pin) return;

    const cards = cardRefs.current.filter(Boolean) as HTMLDivElement[];

    const ctx = gsap.context(() => {
      if (prefersReducedMotion) {
        if (headingRef.current) gsap.set(headingRef.current, { opacity: 1, y: 0 });
        cards.forEach((card, i) => {
          gsap.set(card, { opacity: 1, y: 0, scale: 1 });
          const image = imageRefs.current[i];
          if (image) gsap.set(image, { opacity: 1 });
          if (i > 0) {
            card.style.position = "relative";
            card.style.top = "";
          }
        });
        return;
      }

      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: headingRef.current,
              start: "top 85%",
              end: "top 55%",
              scrub: 0.4,
            },
          }
        );
      }

      const cardCount = cards.length;
      const images = imageRefs.current.filter(Boolean) as HTMLDivElement[];

      if (cardCount === 0) return;

      images.forEach((image, i) => {
        gsap.set(image, { opacity: i === 0 ? 1 : 0 });
      });

      const isMobile = isMobileLayout;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stack,
          start: "top top",
          end: "bottom bottom",
          scrub: isMobile ? 0.5 : 0.4,
          pin,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      tl.eventCallback("onStart", () => setMotionLayers(cards, true));
      tl.eventCallback("onReverseComplete", () => setMotionLayers(cards, false));

      const PHASE = 1 / cardCount;

      cards.forEach((card, i) => {
        if (i > 0) {
          gsap.set(card, {
            scale: 1 - i * CARD_SCALE_STEP,
            y: i * CARD_STAGGER_DESKTOP,
            opacity: stackedCardOpacity(i),
            transformOrigin: "bottom center",
          });
        } else {
          gsap.set(card, { opacity: 1 });
        }

        if (i < cardCount - 1) {
          const exitStart = PHASE * i + PHASE * 0.08;
          const exitDuration = PHASE * 0.58;

          tl.to(
            card,
            {
              yPercent: -120,
              opacity: 0,
              ease: "power1.in",
              duration: exitDuration,
            },
            exitStart
          );

          if (i + 1 < cardCount) {
            tl.to(
              cards[i + 1],
              {
                scale: 1,
                y: 0,
                opacity: 1,
                ease: "power2.out",
                duration: PHASE * 0.5,
              },
              exitStart
            );

            for (let j = i + 2; j < cardCount; j++) {
              tl.to(
                cards[j],
                {
                  opacity: stackedCardOpacity(j - (i + 1)),
                  ease: "power2.out",
                  duration: PHASE * 0.5,
                },
                exitStart
              );
            }

            const nextImage = imageRefs.current[i + 1];
            if (nextImage) {
              tl.to(
                nextImage,
                {
                  opacity: 1,
                  ease: "power2.out",
                  duration: PHASE * 0.5,
                },
                exitStart
              );
            }
          }
        }

        if (i === cardCount - 1) {
          tl.to(
            card,
            {
              opacity: 0.7,
              scale: 0.98,
              y: -10,
              ease: "power1.in",
              duration: PHASE * 0.12,
            },
            1 - PHASE * 0.12
          );
        }
      });

    }, section);

    return () => {
      setMotionLayers(cards, false);
      ctx.revert();
    };
  }, [isMobileLayout, prefersReducedMotion]);

  return (
    <section ref={sectionRef} id="principles" className="relative w-full pt-16 md:pt-[130px]">
      <div className="max-w-[1200px] mx-auto px-5">
        <div
          ref={stackRef}
          className="relative"
          style={{
            height: prefersReducedMotion
              ? "auto"
              : isMobileLayout
                ? `${PRINCIPLES.length * SCROLL_VH_PER_CARD_MOBILE}vh`
                : `${PRINCIPLES.length * SCROLL_VH_PER_CARD_DESKTOP}vh`,
          }}
        >
          <div
            ref={pinRef}
            className="relative z-30 flex min-h-dvh flex-col justify-center max-md:justify-start max-md:pt-24 md:pt-0"
          >
            <div>
              <h2
                ref={headingRef}
                className="text-[clamp(32px,4vw,40px)] font-semibold leading-[1.2] bg-clip-text text-transparent py-2 mb-6 md:mb-[36px]"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #ffffff 0%, #b7a8ea 100%)",
                }}
              >
                Core principles
              </h2>
            </div>
            <div className="relative">
              {PRINCIPLES.map((principle, i) => (
                <div
                  key={principle.title}
                  ref={(el) => { cardRefs.current[i] = el; }}
                  className={`group rounded-2xl ${prefersReducedMotion || i === 0 ? "relative" : "absolute left-0 right-0"}`}
                  style={{
                    top: prefersReducedMotion || i === 0 ? undefined : 0,
                    zIndex: prefersReducedMotion ? undefined : PRINCIPLES.length - i,
                    transformOrigin: "bottom center",
                    marginTop: prefersReducedMotion && i > 0 ? "10px" : undefined,
                  }}
                  onMouseMove={trackHoverGlow}
                >
                  <div className="relative rounded-2xl overflow-hidden bg-[#1A0D53] border border-white/10">
                    <div className="flex flex-col md:flex-row h-[min(480px,70vh)] md:h-[min(520px,65vh)]">
                      <div className="relative overflow-hidden bg-[#1A0D53] md:w-[45%] h-[45%] md:h-full">
                        <div
                          ref={(el) => { imageRefs.current[i] = el; }}
                          className="absolute inset-0"
                          style={{
                            opacity: prefersReducedMotion || i === 0 ? 1 : 0,
                          }}
                        >
                          <OptimizedPicture
                            alt={principle.title}
                            src={principle.image}
                            pictureClassName="absolute inset-0"
                            imgClassName="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col justify-center gap-4 md:gap-6 px-6 py-6 md:px-12 md:py-10 md:w-[55%] h-[55%] md:h-full">
                        <span
                          className="text-[clamp(36px,5.5vw,80px)] font-bold leading-[1] bg-clip-text text-transparent tabular-nums"
                          style={{
                            backgroundImage:
                              "linear-gradient(180deg, #fff 0%, #b7a8ea 100%)",
                          }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="text-xl md:text-2xl lg:text-[28px] font-semibold text-white leading-[1.3]">
                          {principle.title}
                        </h3>
                        <p className="text-sm md:text-base leading-relaxed text-white/70 max-w-md">
                          {principle.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <HoverGlow intensity="soft" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pb-18 md:pb-24 lg:pb-30" />
      </div>
    </section>
  );
}
